import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getCatalogStates, getCatalogCities } from '../../services/api';

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

// Helper function para determinar si el cache es válido
const isCacheValid = (cacheItem, maxAge = 5 * 60 * 1000) => {
  // 5 minutos por defecto
  if (!cacheItem.data || !cacheItem.lastFetched) return false;
  return Date.now() - new Date(cacheItem.lastFetched).getTime() < maxAge;
};

// Estado inicial unificado
const INITIAL_STATE = {
  // Cache para todos los datos de catálogos con metadatos
  cache: {
    genders: {
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,
    },
    occupations: {
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,
    },
    howYouMeet: {
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,
    },
    specialties: {
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,
    },
    languages: {
      data: null,
      lastFetched: null,
      isLoading: false,
      error: null,
    },
    locations: {
      countries: {
        data: null,
        lastFetched: null,
        isLoading: false,
        error: null,
      },
      states: {
        data: {}, // Por país: { countryId: [...states] }
        lastFetched: null,
        isLoading: false,
        error: null,
      },
      cities: {
        data: {}, // Por estado: { stateId: [...cities] }
        lastFetched: null,
        isLoading: false,
        error: null,
      },
    },
  },

  // Estados globales de la API
  apiStatus: {
    isOnline: true,
    lastConnected: null,
    retryCount: 0,
  },

  // Request tracking
  pendingRequests: [],

  // Error handling global
  globalError: null,
  lastError: null,
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const fetchGenders = createAsyncThunk(
  'data/fetchGenders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // Verificar cache
      if (isCacheValid(state.data.cache.genders)) {
        return state.data.cache.genders.data;
      }

      // TODO: Reemplazar con llamada real a la API
      // const response = await getGenders();

      // Mock data por ahora
      const mockData = [
        { id: '1', name: 'Masculino' },
        { id: '2', name: 'Femenino' },
        { id: '3', name: 'Otro' },
        { id: '4', name: 'Prefiero no decir' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching genders');
    }
  }
);

export const fetchOccupations = createAsyncThunk(
  'data/fetchOccupations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.data.cache.occupations)) {
        return state.data.cache.occupations.data;
      }

      // TODO: Reemplazar con llamada real a la API
      // const response = await getOccupations();

      const mockData = [
        { id_ocupacion: '1', ocupation: 'Empleado' },
        { id_ocupacion: '2', ocupation: 'Empresario' },
        { id_ocupacion: '3', ocupation: 'Estudiante' },
        { id_ocupacion: '4', ocupation: 'Profesionista Independiente' },
        { id_ocupacion: '5', ocupation: 'Jubilado' },
        { id_ocupacion: '6', ocupation: 'Otro' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching occupations');
    }
  }
);

export const fetchHowYouMeet = createAsyncThunk(
  'data/fetchHowYouMeet',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.data.cache.howYouMeet)) {
        return state.data.cache.howYouMeet.data;
      }

      // TODO: Reemplazar con llamada real a la API
      // const response = await getHowYouMeet();

      const mockData = [
        { id: '1', name: 'Redes sociales' },
        { id: '2', name: 'Recomendación' },
        { id: '3', name: 'Búsqueda en Google' },
        { id: '4', name: 'Publicidad' },
        { id: '5', name: 'Otro' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching how you meet options');
    }
  }
);

export const fetchSpecialties = createAsyncThunk(
  'data/fetchSpecialties',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.data.cache.specialties)) {
        return state.data.cache.specialties.data;
      }

      // Datos estáticos consolidados (combinando ambas fuentes)
      const staticSpecialties = [
        {
          id: 'derecho-familiar',
          id_especialidad: '1',
          name: 'Derecho Familiar',
          especialidad_name: 'Derecho Familiar',
          description:
            'Especialistas en matrimonios, divorcios, adopciones, custodia de menores, pensiones alimenticias y todos los aspectos legales relacionados con la familia.',
        },
        {
          id: 'derecho-penal',
          id_especialidad: '2',
          name: 'Derecho Penal',
          especialidad_name: 'Derecho Penal',
          description:
            'Asesoría legal para víctimas y acusados en delitos, defensa penal, juicios penales y procedimientos ante el Ministerio Público.',
        },
        {
          id: 'derecho-laboral',
          id_especialidad: '3',
          name: 'Derecho Laboral',
          especialidad_name: 'Derecho Laboral',
          description:
            'Soluciones legales para trabajadores y empresas: despidos, demandas laborales, contratos de trabajo, seguridad social y relaciones obrero-patronales.',
        },
        {
          id: 'derecho-mercantil',
          id_especialidad: '4',
          name: 'Derecho Mercantil',
          especialidad_name: 'Derecho Mercantil',
          description:
            'Asesoría en constitución de empresas, contratos comerciales, fusiones, adquisiciones y toda la legislación empresarial y comercial.',
        },
        {
          id: 'derecho-civil',
          id_especialidad: '5',
          name: 'Derecho Civil',
          especialidad_name: 'Derecho Civil',
          description:
            'Contratos civiles, responsabilidad civil, sucesiones, testamentos, herencias y conflictos entre particulares.',
        },
        {
          id: 'derecho-fiscal',
          id_especialidad: '6',
          name: 'Derecho Fiscal',
          especialidad_name: 'Derecho Fiscal',
          description:
            'Asesoría tributaria, defensa ante el SAT, planeación fiscal, regularización de contribuyentes y optimización de cargas fiscales.',
        },
        {
          id: 'derecho-inmobiliario',
          id_especialidad: '7',
          name: 'Derecho Inmobiliario',
          especialidad_name: 'Derecho Inmobiliario',
          description:
            'Compraventa de propiedades, escrituración, regularización de títulos de propiedad, contratos de arrendamiento y desarrollo inmobiliario.',
        },
        {
          id: 'derecho-corporativo',
          id_especialidad: '8',
          name: 'Derecho Corporativo',
          especialidad_name: 'Derecho Corporativo',
          description:
            'Gobierno corporativo, compliance, restructuras empresariales, joint ventures y asesoría legal integral para grandes corporaciones.',
        },
      ];

      // Simular delay de red para mantener consistencia
      await new Promise(resolve => setTimeout(resolve, 100));

      return staticSpecialties;

      // TODO: Cambiar a backend cuando esté disponible
      // const response = await getLawyerSpecialties();
      // return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching specialties');
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  'data/fetchLanguages',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.data.cache.languages)) {
        return state.data.cache.languages.data;
      }

      // Datos estáticos consolidados (manteniendo compatibilidad con ambas estructuras)
      const staticLanguages = [
        { id: '1', id_language: '1', name: 'Español', language: 'Español', code: 'es' },
        { id: '2', id_language: '2', name: 'Inglés', language: 'Inglés', code: 'en' },
        { id: '3', id_language: '3', name: 'Francés', language: 'Francés', code: 'fr' },
        { id: '4', id_language: '4', name: 'Alemán', language: 'Alemán', code: 'de' },
        { id: '5', id_language: '5', name: 'Portugués', language: 'Portugués', code: 'pt' },
        { id: '6', id_language: '6', name: 'Italiano', language: 'Italiano', code: 'it' },
        { id: '7', id_language: '7', name: 'Chino', language: 'Chino', code: 'zh' },
        { id: '8', id_language: '8', name: 'Japonés', language: 'Japonés', code: 'ja' },
        { id: 'otro', id_language: 'otro', name: 'Otro', language: 'Otro', code: 'other' },
      ];

      // Simular delay de red para mantener consistencia
      await new Promise(resolve => setTimeout(resolve, 100));

      return staticLanguages;

      // TODO: Cambiar a backend cuando esté disponible
      // const response = await getLawyerLanguages();
      // return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching languages');
    }
  }
);

export const fetchCountries = createAsyncThunk(
  'data/fetchCountries',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.data.cache.locations.countries)) {
        return state.data.cache.locations.countries.data;
      }

      // TODO: Reemplazar con llamada real a la API
      const mockData = [
        { id: 142, name: 'México', code: 'MX' },
        { id: 1, name: 'Estados Unidos', code: 'US' },
        { id: 2, name: 'España', code: 'ES' },
        { id: 3, name: 'Colombia', code: 'CO' },
        { id: 4, name: 'Argentina', code: 'AR' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching countries');
    }
  }
);

export const fetchStates = createAsyncThunk(
  'data/fetchStates',
  async (countryId = 142, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // Verificar cache para este país específico
      const cachedStates = state.data.cache.locations.states.data[countryId];
      if (cachedStates && isCacheValid(state.data.cache.locations.states)) {
        return { countryId, data: cachedStates };
      }

      // Llamada real a la API
      const response = await getCatalogStates(countryId);
      return { countryId, data: response };
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching states');
    }
  }
);

export const fetchCities = createAsyncThunk(
  'data/fetchCities',
  async (stateId, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // Verificar cache para este estado específico
      const cachedCities = state.data.cache.locations.cities.data[stateId];
      if (cachedCities && isCacheValid(state.data.cache.locations.cities)) {
        return { stateId, data: cachedCities };
      }

      // Llamada real a la API
      const response = await getCatalogCities(stateId);
      return { stateId, data: response };
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching cities');
    }
  }
);

// Async thunk para inicializar datos esenciales
export const initializeData = createAsyncThunk('data/initializeData', async (_, { dispatch }) => {
  try {
    await Promise.all([
      dispatch(fetchGenders()),
      dispatch(fetchOccupations()),
      dispatch(fetchHowYouMeet()),
      dispatch(fetchSpecialties()),
      dispatch(fetchLanguages()),
      dispatch(fetchCountries()),
    ]);
    return true;
  } catch (error) {
    throw error;
  }
});

// ============================================================================
// SLICE
// ============================================================================

const dataSlice = createSlice({
  name: 'data',
  initialState: INITIAL_STATE,
  reducers: {
    // Cache Management
    clearCache: state => {
      Object.keys(state.cache).forEach(key => {
        if (key === 'locations') {
          // Handle nested locations
          Object.keys(state.cache.locations).forEach(locKey => {
            state.cache.locations[locKey] = {
              data: locKey === 'states' || locKey === 'cities' ? {} : null,
              lastFetched: null,
              isLoading: false,
              error: null,
            };
          });
        } else {
          state.cache[key] = {
            data: null,
            lastFetched: null,
            isLoading: false,
            error: null,
          };
        }
      });
    },

    clearCacheItem: (state, action) => {
      const item = action.payload;
      if (state.cache[item]) {
        state.cache[item] = {
          data: null,
          lastFetched: null,
          isLoading: false,
          error: null,
        };
      }
    },

    // Specific cache clearing methods
    clearCities: state => {
      state.cache.locations.cities = {
        data: {},
        lastFetched: null,
        isLoading: false,
        error: null,
      };
    },

    clearErrors: state => {
      Object.keys(state.cache).forEach(key => {
        if (key === 'locations') {
          Object.keys(state.cache.locations).forEach(locKey => {
            state.cache.locations[locKey].error = null;
          });
        } else {
          state.cache[key].error = null;
        }
      });
      state.globalError = null;
    },

    // API Status
    setApiOnlineStatus: (state, action) => {
      state.apiStatus.isOnline = action.payload;
      if (action.payload) {
        state.apiStatus.lastConnected = new Date().toISOString();
        state.apiStatus.retryCount = 0;
      }
    },

    incrementRetryCount: state => {
      state.apiStatus.retryCount += 1;
    },

    resetRetryCount: state => {
      state.apiStatus.retryCount = 0;
    },

    // Error Handling
    setGlobalError: (state, action) => {
      state.globalError = action.payload;
      state.lastError = {
        error: action.payload,
        timestamp: new Date().toISOString(),
      };
    },

    clearGlobalError: state => {
      state.globalError = null;
    },

    // Request Tracking
    addPendingRequest: (state, action) => {
      state.pendingRequests.push({
        id: action.payload.id,
        type: action.payload.type,
        timestamp: new Date().toISOString(),
      });
    },

    removePendingRequest: (state, action) => {
      state.pendingRequests = state.pendingRequests.filter(req => req.id !== action.payload);
    },

    clearPendingRequests: state => {
      state.pendingRequests = [];
    },
  },

  extraReducers: builder => {
    // Helper para manejar estados de carga estándar
    const handleAsyncState = (cacheKey, isNested = false) => ({
      pending: state => {
        if (isNested) {
          const keys = cacheKey.split('.');
          let current = state.cache;
          keys.forEach(key => (current = current[key]));
          current.isLoading = true;
          current.error = null;
        } else {
          state.cache[cacheKey].isLoading = true;
          state.cache[cacheKey].error = null;
        }
      },
      fulfilled: (state, action) => {
        if (isNested) {
          const keys = cacheKey.split('.');
          let current = state.cache;
          keys.forEach(key => (current = current[key]));
          current.isLoading = false;
          current.data = action.payload;
          current.lastFetched = new Date().toISOString();
        } else {
          state.cache[cacheKey].isLoading = false;
          state.cache[cacheKey].data = action.payload;
          state.cache[cacheKey].lastFetched = new Date().toISOString();
        }
      },
      rejected: (state, action) => {
        if (isNested) {
          const keys = cacheKey.split('.');
          let current = state.cache;
          keys.forEach(key => (current = current[key]));
          current.isLoading = false;
          current.error = action.payload;
        } else {
          state.cache[cacheKey].isLoading = false;
          state.cache[cacheKey].error = action.payload;
        }
      },
    });

    // Genders
    const genderHandlers = handleAsyncState('genders');
    builder
      .addCase(fetchGenders.pending, genderHandlers.pending)
      .addCase(fetchGenders.fulfilled, genderHandlers.fulfilled)
      .addCase(fetchGenders.rejected, genderHandlers.rejected);

    // Occupations
    const occupationHandlers = handleAsyncState('occupations');
    builder
      .addCase(fetchOccupations.pending, occupationHandlers.pending)
      .addCase(fetchOccupations.fulfilled, occupationHandlers.fulfilled)
      .addCase(fetchOccupations.rejected, occupationHandlers.rejected);

    // How You Meet
    const howYouMeetHandlers = handleAsyncState('howYouMeet');
    builder
      .addCase(fetchHowYouMeet.pending, howYouMeetHandlers.pending)
      .addCase(fetchHowYouMeet.fulfilled, howYouMeetHandlers.fulfilled)
      .addCase(fetchHowYouMeet.rejected, howYouMeetHandlers.rejected);

    // Specialties
    const specialtyHandlers = handleAsyncState('specialties');
    builder
      .addCase(fetchSpecialties.pending, specialtyHandlers.pending)
      .addCase(fetchSpecialties.fulfilled, specialtyHandlers.fulfilled)
      .addCase(fetchSpecialties.rejected, specialtyHandlers.rejected);

    // Languages
    const languageHandlers = handleAsyncState('languages');
    builder
      .addCase(fetchLanguages.pending, languageHandlers.pending)
      .addCase(fetchLanguages.fulfilled, languageHandlers.fulfilled)
      .addCase(fetchLanguages.rejected, languageHandlers.rejected);

    // Countries
    const countryHandlers = handleAsyncState('locations.countries', true);
    builder
      .addCase(fetchCountries.pending, countryHandlers.pending)
      .addCase(fetchCountries.fulfilled, countryHandlers.fulfilled)
      .addCase(fetchCountries.rejected, countryHandlers.rejected);

    // States (special handling for country-specific data)
    builder
      .addCase(fetchStates.pending, state => {
        state.cache.locations.states.isLoading = true;
        state.cache.locations.states.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.cache.locations.states.isLoading = false;
        state.cache.locations.states.data[action.payload.countryId] = action.payload.data;
        state.cache.locations.states.lastFetched = new Date().toISOString();
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.cache.locations.states.isLoading = false;
        state.cache.locations.states.error = action.payload;
      });

    // Cities (special handling for state-specific data)
    builder
      .addCase(fetchCities.pending, state => {
        state.cache.locations.cities.isLoading = true;
        state.cache.locations.cities.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cache.locations.cities.isLoading = false;
        state.cache.locations.cities.data[action.payload.stateId] = action.payload.data;
        state.cache.locations.cities.lastFetched = new Date().toISOString();
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.cache.locations.cities.isLoading = false;
        state.cache.locations.cities.error = action.payload;
      });

    // Initialize Data
    builder.addCase(initializeData.fulfilled, state => {
      state.apiStatus.lastConnected = new Date().toISOString();
    });
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

// Export actions
export const {
  clearCache,
  clearCacheItem,
  clearCities,
  clearErrors,
  setApiOnlineStatus,
  incrementRetryCount,
  resetRetryCount,
  setGlobalError,
  clearGlobalError,
  addPendingRequest,
  removePendingRequest,
  clearPendingRequests,
} = dataSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

// Base selectors
export const selectDataCache = state => state.data.cache;
export const selectApiStatus = state => state.data.apiStatus;
export const selectGlobalError = state => state.data.globalError;
export const selectPendingRequests = state => state.data.pendingRequests;

// Specific cache selectors (maintaining backward compatibility)
export const selectGenders = state => state.data.cache.genders;
export const selectOccupations = state => state.data.cache.occupations;
export const selectHowYouMeet = state => state.data.cache.howYouMeet;
export const selectSpecialties = state => state.data.cache.specialties;
export const selectLanguages = state => state.data.cache.languages;
export const selectCountries = state => state.data.cache.locations.countries;

// Location selectors with backward compatibility - MEMOIZED
export const selectStates = createSelector(
  [state => state.data.cache.locations.states],
  statesCache => ({
    data: Object.values(statesCache.data).flat(),
    loading: statesCache.isLoading,
    error: statesCache.error,
  })
);

export const selectCities = createSelector(
  [state => state.data.cache.locations.cities],
  citiesCache => ({
    data: Object.values(citiesCache.data).flat(),
    loading: citiesCache.isLoading,
    error: citiesCache.error,
  })
);

// Para compatibilidad con apiSlice
export const selectStatesByCountry = countryId => state =>
  state.data.cache.locations.states.data[countryId] || [];

export const selectCitiesByState = stateId => state =>
  state.data.cache.locations.cities.data[stateId] || [];

// Specialty selector by ID (maintaining catalogSlice compatibility)
export const selectSpecialtyById = (state, specialtyId) =>
  state.data.cache.specialties.data?.find(
    specialty => specialty.id === specialtyId || specialty.id_especialidad === specialtyId
  );

// Computed selectors
export const selectIsDataLoaded = state => {
  const cache = state.data.cache;
  return (
    cache.genders.data &&
    cache.occupations.data &&
    cache.howYouMeet.data &&
    cache.specialties.data &&
    cache.languages.data &&
    cache.locations.countries.data
  );
};

export const selectIsDataLoading = state => {
  const cache = state.data.cache;
  return (
    cache.genders.isLoading ||
    cache.occupations.isLoading ||
    cache.howYouMeet.isLoading ||
    cache.specialties.isLoading ||
    cache.languages.isLoading ||
    cache.locations.countries.isLoading ||
    cache.locations.states.isLoading ||
    cache.locations.cities.isLoading
  );
};

export default dataSlice.reducer;
